import React from 'react'
import { yupResolver } from '@hookform/resolvers/yup'
import * as R from 'ramda'
import { useDispatch } from 'react-redux'
import { useControlledForm, useFormApiError, useFormReset } from 'hooks'
import { users } from 'orm'
import { mapPayloadToInternalState } from './actions'
import { getSchema } from './schema'
import { defaultValues, DEPARTMENT_FIELD, ROLE_FIELD } from './model'
import { ADMIN_ROLE } from 'constants/users'

const useManageUserForm = (state, handlers) => {
  const { user, isOpen } = state
  const { onConfirm } = handlers

  const [apiError, setApiError] = React.useState(null)
  const [shouldReset, setShouldReset] = React.useState(false)
  const [showPasswordField, setShowPasswordField] = React.useState(false)
  const [isPasswordVisible, setIsPasswordVisible] = React.useState(false)

  const schema = getSchema(!!user)
  const dispatch = useDispatch()

  const internalState = React.useMemo(() => mapPayloadToInternalState(user), [
    user,
  ])

  const handleFormData = React.useCallback(
    async data => {
      await onConfirm(data, {
        onError: setApiError,
        onSuccess: () => setShouldReset(true),
      })
    },
    [onConfirm, setApiError],
  )

  React.useEffect(() => {
    dispatch(users.fetchStart())
  }, [dispatch])

  const {
    control,
    formState,
    handleFormApiError,
    handleFormReset,
    handleFormSubmit,
    setValue,
    watch,
  } = useControlledForm({
    values: user ? internalState : defaultValues,
    resolver: yupResolver(schema),
    handleFormData,
  })

  const role = watch(ROLE_FIELD)

  React.useEffect(() => {
    const isAdminSelected = ADMIN_ROLE == watch(ROLE_FIELD)?.value

    if (isAdminSelected) {
      setValue(DEPARTMENT_FIELD, [])
    }
  }, [role])

  useFormApiError({ apiError }, handleFormApiError)
  useFormReset(
    {
      shouldReset: R.and(shouldReset, formState.isSubmitSuccessful),
    },
    handleFormReset,
  )

  React.useEffect(() => {
    if (isOpen) {
      setShouldReset(false)
      setShowPasswordField(!user)
    }
  }, [isOpen, user])

  return {
    state: {
      control,
      formState,
      showPasswordField,
      isPasswordVisible,
    },
    handlers: {
      handleFormReset,
      handleFormSubmit,
      setShowPasswordField,
      watch,
      setIsPasswordVisible,
    },
  }
}

export const useModal = (state, handlers) => {
  const { user, isOpen } = state
  const { onCancel, onClose, onConfirm } = handlers

  const {
    state: { control, formState, showPasswordField, isPasswordVisible },
    handlers: {
      handleFormReset,
      handleFormSubmit,
      setShowPasswordField,
      watch,
      setIsPasswordVisible,
    },
  } = useManageUserForm({ user, isOpen }, { onConfirm })

  const handleResetState = React.useCallback(() => {
    handleFormReset()
    setShowPasswordField(false)
    setIsPasswordVisible(false)
  }, [handleFormReset])

  const handleCancel = React.useCallback(() => {
    handleResetState()
    onCancel()
  }, [handleResetState, onCancel])

  const handleClose = React.useCallback(() => {
    handleResetState()
    onClose()
  }, [handleResetState, onClose])

  return {
    handlers: {
      handleCancel,
      handleClose,
      watch,
      handleFormSubmit,
      setShowPasswordField,
      setIsPasswordVisible,
    },
    state: {
      control,
      formState,
      showPasswordField,
      isPasswordVisible,
    },
  }
}
